import axios, { AxiosInstance } from 'axios';
import { TypeCheck } from '../utils/TypeCheck';

export interface ErrorCallback {
  onErrorAccessDenied: (action?: string) => void;
  onErrorLogout: () => void;
  onErrorRedirect: () => void;
  onErrorDenied: () => void;
  onErrorBlockUser: () => void;
}

export interface NetworkCallback {
  onComplete: (res: any) => void;
  onError: (err: any) => void;
}

declare module 'axios' {
  export interface AxiosInstance {
    request<T = any>(config: AxiosRequestConfig): Promise<T>;
    get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    head<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    post<T = any>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig,
    ): Promise<T>;
    put<T = any>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig,
    ): Promise<T>;
    patch<T = any>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig,
    ): Promise<T>;
  }
}

export interface ApiManagerDefault {
  version: string;
  baseUrl: string;
  apiKey: string; // api 통신시 사용되는 key
  uuid: string; // cra에서 사용되는 고유값 (storage 초기화시 변경됨)
  ua?: string; // app은 package name 기본은 web으로
  partnerCode?: string | null; // cra에서만 사용되는 key
  packageName?: string;
  language: string;
  userId?: string;
}

export interface DefaultInterceptorCallback {
  callback: (locale: string, localeString: string) => void;
  callbackServerVersion: (version: string) => void;
  callbackUserId: (userId: string) => void;
}

export abstract class ApiManager {
  protected readonly instance: AxiosInstance;

  public constructor(initValue: ApiManagerDefault) {
    // 임시로 any타입 지정
    // axios.defaults.baseURL = process.env.BASE_URL;
    // axios.defaults.headers.common['x-api-key'] = process.env.API_KEY;
    // axios.defaults.headers.common.deviceId = UserDefault.getUdid();
    axios.defaults.baseURL = initValue.baseUrl;

    var topcoHeader: any = {
      'x-api-key': initValue.apiKey,
      version: initValue.version,
      deviceId: initValue.uuid,
      partnerCode: `${initValue.partnerCode}`,
      ua: `${initValue.ua}`,
      language: `${initValue.language}`,
      'user-id': `${initValue.userId}`,
    };

    // 앱 전용 헤더 추가됨
    if (initValue.packageName) {
      topcoHeader = {
        ...topcoHeader,
        'package-name': `${initValue.packageName}`,
      };
    }

    axios.defaults.headers.common = topcoHeader;

    if (initValue.partnerCode === undefined) {
      delete axios.defaults.headers.common.partnerCode;
    }

    if (initValue.ua === undefined) {
      delete axios.defaults.headers.common.ua;
    }

    axios.defaults.timeout = 30 * 1000;
    this.instance = axios;
    // this.defaultInterceptor();
  }

  // 전역 설정
  public defaultInterceptor = (cb: DefaultInterceptorCallback) => {
    axios.interceptors.response.use(
      response => {
        const locale = TypeCheck.itemsByPath(response, 'headers.locale');
        const version = TypeCheck.itemsByPath(response, 'headers.version');
        const userId: string | null | undefined = TypeCheck.itemsByPath(
          response,
          'headers.user-id',
        );

        // console.log('userId', userId, response);
        if (userId) {
          cb.callbackUserId(userId);
        }

        // 서버 버전 전달
        cb.callbackServerVersion(version);

        if (locale) {
          // 한국의 경우 성인 체크버튼 비노출 (정책 적용) 0 비대상 1 대상
          const result = locale.toLowerCase() === 'kr' ? '0' : '1';
          // UserDefault.setNormalMode(result);
          cb.callback(result, locale.toLowerCase());
        }
        return response;
      },
      error => {
        return Promise.reject(error);
      },
    );
  };

  /**
   * 관리자용 interceptor
   * @param errorCallback
   */
  public managerInterceptor = (errorCallback: ErrorCallback) => {
    axios.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        // logout 처리
        if (error.response?.status === 401) {
          errorCallback.onErrorLogout();
        }

        return Promise.reject(error);
      },
    );
  };

  // 사용자 로그아웃 처리
  public userInterceptor = (errorCallback: ErrorCallback) => {
    axios.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        // 접근 에러 (성인인증, ipblock)
        if (error.response?.status === 461) {
          const { action } = error.response.data;
          errorCallback.onErrorAccessDenied(action);
          return;
        }

        // logout 처리
        if (error.response?.status === 463) {
          errorCallback.onErrorLogout();
        }

        // 유출자 경고 페이지 이동
        if (error.response?.status === 465) {
          errorCallback.onErrorBlockUser();
        }

        if (error.response?.status === 470) {
          errorCallback.onErrorRedirect();
        }

        // 유출자 & 의심자 회차리스트 접근
        if (error.response?.status === 511) {
          errorCallback.onErrorDenied();
        }

        return Promise.reject(error);
      },
    );
  };
}
